'use client'

import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { cn } from '@/utils/styles/cn'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, Cross2Icon } from '@radix-ui/react-icons'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
        clearable?: boolean
        onClear?: () => void
        childrenClassName?: string
        withChevron?: boolean
        variant?: 'default' | 'dark'
    }
>(
    (
        {
            className,
            children,
            clearable,
            onClear,
            childrenClassName,
            withChevron = true,
            variant = 'default',
            ...props
        },
        ref
    ) => {
        const variantStyles =
            variant === 'dark'
                ? 'bg-transparent border-[#FFFFFF14] text-[#FFFFFFF2] rounded-md hover:bg-linear-to-r hover:from-[#313136] hover:to-[#35353A] data-[state=open]:bg-linear-to-r data-[state=open]:from-[#313136] data-[state=open]:to-[#35353A] shadow-none'
                : 'bg-transparent border-input shadow-xs'

        return (
            <div className={cn('relative', className)}>
                {clearable && (
                    <button
                        data-clear-button
                        onClick={onClear}
                        className="absolute right-8 top-1/2 -translate-y-1/2 z-10"
                    >
                        <Cross2Icon className="h-3.5 w-3.5 opacity-50 hover:opacity-100" />
                    </button>
                )}
                <SelectPrimitive.Trigger
                    ref={ref}
                    className={cn(
                        'relative flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border px-3 py-2 text-sm ring-offset-background data-placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
                        variantStyles,
                        className
                    )}
                    {...props}
                >
                    <div className={cn('w-full text-start truncate overflow-hidden pr-6', childrenClassName)}>
                        {children}
                    </div>

                    {withChevron && (
                        <SelectPrimitive.Icon asChild>
                            <ChevronDownIcon className="h-4 min-h-4 min-w-4 opacity-70 group-hover:opacity-100" />
                        </SelectPrimitive.Icon>
                    )}
                </SelectPrimitive.Trigger>
            </div>
        )
    }
)
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollUpButton
        ref={ref}
        className={cn('flex cursor-default items-center justify-center py-1', className)}
        {...props}
    >
        <ChevronUpIcon className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollDownButton
        ref={ref}
        className={cn('flex cursor-default items-center justify-center py-1', className)}
        {...props}
    >
        <ChevronDownIcon className="h-4 w-4" />
    </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
        emptyText?: string
        variant?: 'default' | 'dark'
    }
>(
    (
        { className, children, position = 'popper', emptyText = 'No options available', variant = 'default', ...props },
        ref
    ) => {
        const variantStyles =
            variant === 'dark'
                ? 'min-w-[134px] max-w-[280px] rounded-[6px] border border-[#FFFFFF14] bg-[#27272A] shadow-[0px_1px_0px_0px_rgba(255,255,255,0.20)_inset,0px_1px_2px_0px_rgba(0,0,0,0.40),0px_0px_0px_1px_#18181B]'
                : 'relative z-50 max-h-96 min-w-32 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md'

        return (
            <SelectPrimitive.Portal>
                <SelectPrimitive.Content
                    ref={ref}
                    className={cn(
                        variantStyles,
                        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                        position === 'popper' &&
                            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
                        className
                    )}
                    position={position}
                    {...props}
                >
                    <SelectScrollUpButton />
                    <SelectPrimitive.Viewport
                        className={cn(
                            'p-1',
                            position === 'popper' &&
                                'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)'
                        )}
                    >
                        {React.Children.count(children) === 0 ? (
                            <span className="block py-2 px-2 text-sm text-muted-foreground">{emptyText}</span>
                        ) : (
                            children
                        )}
                    </SelectPrimitive.Viewport>
                    <SelectScrollDownButton />
                </SelectPrimitive.Content>
            </SelectPrimitive.Portal>
        )
    }
)
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label ref={ref} className={cn('px-2 py-1.5 text-sm font-semibold', className)} {...props} />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
        variant?: 'default' | 'dark'
    }
>(({ className, children, variant = 'default', ...props }, ref) => {
    const variantStyles =
        variant === 'dark'
            ? 'text-[#FFFFFFF2] focus:bg-[#313136]! focus:text-[#FFFFFFF2]! data-highlighted:bg-[#313136]! data-[state=open]:bg-[#313136]! data-highlighted:bg-linear-to-r data-highlighted:from-[#313136] data-highlighted:to-[#35353A] data-highlighted:text-[#FFFFFFF2]'
            : 'focus:bg-accent focus:text-accent-foreground'

    return (
        <SelectPrimitive.Item
            ref={ref}
            className={cn(
                'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-hidden data-disabled:pointer-events-none data-disabled:opacity-50',
                variantStyles,
                className
            )}
            {...props}
        >
            <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                <SelectPrimitive.ItemIndicator>
                    <CheckIcon className="h-4 w-4" />
                </SelectPrimitive.ItemIndicator>
            </span>
            <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </SelectPrimitive.Item>
    )
})
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-muted', className)} {...props} />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
    Select,
    SelectGroup,
    SelectValue,
    SelectTrigger,
    SelectContent,
    SelectLabel,
    SelectItem,
    SelectSeparator,
    SelectScrollUpButton,
    SelectScrollDownButton,
}
